/// <amd-module name="Wit/Common/KanbanBoardSettings/Components/BoardStatusBadgeView" />
import { IBoardBadgeService } from "Agile/Board/Badge/Services/BoardBadgeService";
import * as React from "react";
import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableValue, ObservableValue, useObservable } from "VSS/Core/Observable";
import { usePageContext } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { ClipboardButton } from "VSSUI/Clipboard";
import { FormItem } from "VSSUI/FormItem";
import { Header, TitleSize } from "VSSUI/Header";
import { Image } from "VSSUI/Image";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { TextField } from "VSSUI/TextField";
import { BoardBadgeColumnOptions } from "Wit/Clients/Work/Contracts/Work";
import * as Resources from "Wit/Common/KanbanBoardSettings/Resources";
import { ISettingsView } from "Wit/Common/WitSettingsPanelTypes/Components/WitSettingsPanel.props";

export function getBoardStatusBadgeTab(
    isStatusBadgePublic: boolean,
    teamId: string,
    backlogLevel: string,
    boardId: string,
    disabled: boolean,
    groupId?: string,
    saveSettings?: (isBadgePublic: boolean) => Promise<void>
): ISettingsView {
    const isDirty = new ObservableValue(false);
    const isValid = new ObservableValue(true);
    const isStatusBadgePublicObservable = new ObservableValue(isStatusBadgePublic);
    return {
        groupId,
        isDirty: isDirty,
        isValid: isValid,
        name: Resources.StatusBadgeTab,
        onSaveSettings: saveSettings ? () => saveSettings(isStatusBadgePublicObservable.value) : undefined,
        renderContent: () => (
            <BoardStatusBadgeView
                backlogLevel={backlogLevel}
                boardId={boardId}
                disabled={disabled}
                isDirty={isDirty}
                isStatusBadgePublic={isStatusBadgePublicObservable}
                originalIsStatusBadgePublic={isStatusBadgePublic}
                teamId={teamId}
            />
        )
    };
}

interface IBoardStatusBadgeViewViewProps {
    backlogLevel: string;
    boardId: string;
    disabled: boolean;
    isDirty: IObservableValue<boolean>;
    isStatusBadgePublic: IObservableValue<boolean>;
    originalIsStatusBadgePublic: boolean;
    teamId: string;
}

const BoardStatusBadgeView: React.FunctionComponent<IBoardStatusBadgeViewViewProps> = ({
    backlogLevel,
    boardId,
    disabled,
    isDirty,
    isStatusBadgePublic,
    originalIsStatusBadgePublic,
    teamId
}) => {
    const pageContext = usePageContext();
    const projectId = getTFSProject(pageContext)?.id ?? "";
    const badgeService = pageContext.getService<IBoardBadgeService>("IBoardBadgeService");
    const [selectedColumnOption] = useObservable<string>(BoardBadgeColumnOptions.InProgressColumns.toString());
    const getUpdatedBadgeUrl = (columnOption: BoardBadgeColumnOptions) =>
        badgeService.getBoardBadgeInfo(projectId, teamId, backlogLevel, boardId, columnOption);
    const [badgeUrl, setBadgeUrl] = React.useState(getUpdatedBadgeUrl(Number(selectedColumnOption.value)));

    const updateIsDirty = () => {
        const updatedIsDirty = originalIsStatusBadgePublic !== isStatusBadgePublic.value;
        if (updatedIsDirty !== isDirty.value) {
            isDirty.value = updatedIsDirty;
        }
    };

    return (
        <div className="flex-column rhythm-vertical-24">
            <Header
                className="padding-vertical-0"
                description={Resources.StatusBadgeTabDescription}
                title={Resources.StatusBadgeTab}
                titleSize={TitleSize.Small}
            />
            <div className="flex-column rhythm-vertical-24 padding-horizontal-20">
                <div className="flex-column rhythm-vertical-8">
                    <Checkbox
                        checked={isStatusBadgePublic}
                        disabled={disabled}
                        label={Resources.StatusBadgeAllowAnonymousUsers}
                        onChange={(event, checked) => {
                            isStatusBadgePublic.value = checked;
                            updateIsDirty();
                        }}
                    />
                </div>
                <div className="flex-column rhythm-vertical-8">
                    <span className="font-weight-semibold">{Resources.StatusBadgeSettings}</span>
                    <span className="secondary-text">{Resources.StatusBadgeSettingsDescription}</span>
                    <RadioButtonGroup
                        className="flex-noshrink"
                        onSelect={buttonId => {
                            selectedColumnOption.value = buttonId;
                            setBadgeUrl(getUpdatedBadgeUrl(Number(selectedColumnOption.value)));
                            updateIsDirty();
                        }}
                        selectedButtonId={selectedColumnOption}
                    >
                        <RadioButton id={BoardBadgeColumnOptions.InProgressColumns.toString()} text={Resources.StatusBadgeInProgressColumns} />
                        <RadioButton id={BoardBadgeColumnOptions.AllColumns.toString()} text={Resources.StatusBadgeAllColumns} />
                    </RadioButtonGroup>
                </div>
                <div className="flex-column rhythm-vertical-8">
                    <span className="font-weight-semibold">{Resources.StatusBadgePreview}</span>
                    <span>
                        <Image alt={Resources.StatusBadgeAltText} src={badgeUrl.badgeUrl} />
                    </span>
                </div>
                <div className="flex-column rhythm-vertical-8">
                    <FormItem label={<span className="font-weight-semibold">{Resources.StatusBadgeSampleMarkdown}</span>}>
                        <span className="flex-row flex-grow">
                            <TextField className="flex-grow" containerClassName="flex-grow" readOnly value={badgeUrl.badgeMarkdown} />
                            <ClipboardButton className="margin-left-4" getContent={() => badgeUrl.badgeMarkdown} />
                        </span>
                    </FormItem>
                </div>
                <div className="flex-column rhythm-vertical-8">
                    <FormItem label={<span className="font-weight-semibold">{Resources.StatusBadgeImageUrl}</span>}>
                        <span className="flex-row flex-grow">
                            <TextField className="flex-grow" containerClassName="flex-grow" readOnly value={badgeUrl.badgeUrl} />
                            <ClipboardButton className="margin-left-4" getContent={() => badgeUrl.badgeUrl} />
                        </span>
                    </FormItem>
                </div>
                <span>{Resources.StatusBadgeInfo}</span>
            </div>
        </div>
    );
};
